import React from "react";
import { FieldValues } from "react-hook-form";

import { useBoolean } from "@chakra-ui/react";

import { ButtonModal, JsonForm, JsonFormControl } from "@components";
import { Alert } from "@chakra-ui/react";

import {
  AuxiliaryComponent,
  MODULE_LOCATIONS,
  PartTypeDefinitionsAux,
  PossibleLocations,
  getHumanReadableAuxPartType,
} from "@constants";
import { useAuth, useCustomToast, useCreateAuxiliaryComponent } from "@hooks";

import { convertDateToTimestamp } from "@utils";

const REGISTER_NEW_COMPONENT_FORM_FIELDS = (
  partLocation: PossibleLocations | undefined,
) =>
  [
    {
      label: "Aux. Component Serial",
      inputType: "text",
      fieldName: "id",
      defaultValue: undefined,
      config: {
        required: "Component ID is required.",
      },
      autocomplete: "off",
    },
    {
      label: "Component Type",
      inputType: "select",
      fieldName: "componentType",
      defaultValue: undefined,
      options: PartTypeDefinitionsAux.map((category) => ({
        label: getHumanReadableAuxPartType(category),
        value: category,
      })),
      config: {
        required: "Component Type is required.",
      },
    },
    {
      label: "Location",
      inputType: "select",
      fieldName: "location",
      defaultValue: partLocation,
      options: Object.values(MODULE_LOCATIONS).map((location) => ({
        label: location,
        value: location,
      })),
      config: {
        required: "Location is required.",
      },
    },
  ] as JsonFormControl[];

export function NewAuxComponentModal() {
  const { user } = useAuth();
  const { createAuxiliaryComponent } = useCreateAuxiliaryComponent();
  const fieldsWithDefault = React.useMemo(
    () => REGISTER_NEW_COMPONENT_FORM_FIELDS(user?.location),
    [user],
  );
  const [isSubmitting, { on: submitStart, off: submitEnd }] = useBoolean(false);

  const componentAddedToast = useCustomToast(() => (
    <Alert status="success">Component Added!</Alert>
  ));

  const submit = React.useCallback(async (data: FieldValues) => {
    submitStart();
    const authId = user?.firebaseUser?.uid;
    const name = user ? `${user.firstName} ${user.lastName}` : undefined;
    if (!authId || !name) throw new Error("User not logged in.");
    const registeredOn = convertDateToTimestamp(new Date());
    const status = "OKAY";
    const newAuxComponent = {
      ...data,
      componentCategory: (
        data["componentType"].split(".")[1] || ""
      ).toLowerCase(),
      registeredBy: { authId, name },
      registeredOn,
      status,
    } as AuxiliaryComponent;
    await createAuxiliaryComponent(newAuxComponent);
    componentAddedToast();
    submitEnd();
  }, []);
  return (
    <ButtonModal
      size="sm"
      title="Register New Auxiliary Component"
      label="Register New"
    >
      <JsonForm
        fields={fieldsWithDefault}
        onSubmit={submit}
        isLoading={isSubmitting}
        submitButtonText="Register"
      />
    </ButtonModal>
  );
}
